import React, { useEffect } from "react";
import { StyledGridRow, StyledGridCol } from "@/common/styles";
import SecureFormItem from "@/common/SecureFormItem";
import TextField from "@/common/elements/InputElements/TextField";
import Select from "@/common/elements/InputElements/Select";
import { generateRules, getOptionsFromRefList } from "@/util/common_function";
import { useSelector } from "react-redux";
import { useFieldVerification } from "@/contexts/FieldVerificationContext";
import { useVocab } from "@/hooks/useVocab";

// NOTE: No <FieldSecurityProvider> needed here — this component is always
// rendered inside <InmateAssociatesForm> which already provides
// entity="InmateAssociates". SecureFormItem reads the context automatically.
// Same goes for field verification — this is shared by Associates, Victim
// Notification, and Movement Rules, each of which mounts its own
// <FieldVerificationContext.Provider>, so useFieldVerification() here always
// resolves to whichever module rendered this instance.

// ASP's default ClientParams('RegularExpression', 'Bio:Phone'/'Bio:Daytime_Phone')
// row — exact ###-###-#### shape, not just "digits and dashes somewhere". Kept
// as an explicit fallback alongside generateRules() below the same way
// AddressesForm.jsx's Zip Code field pairs a hardcoded validator with its own
// generateRules("Addresses:AddressZip", ...) call.
const phonePattern = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
const phoneMessage =
  "Phone numbers can only contain numbers and dashes must be in the format '###-###-####'.";
const JACKET_COPIED_FIELDS = ["lName", "fName", "mName", "suffix", "daytimePhone", "phone"];

const PersonalBasicInformation = ({
  selectedAssociate,
  form,
  startingPoint,
  // True only when selectedAssociate was just populated from a fresh jacket
  // pick (FolderDetailSearchModal), not from editing an already-saved
  // associate/victim/movement-rule row — callers own that distinction since
  // they're the ones with both code paths.
  fromJacketSearch = false,
}) => {
  const fullSearch = useSelector((state) => state.fullSearch);
  const { markUnverified } = useFieldVerification();
  // Labels below are PCI's defaults; a site's Vocabulary rows rename them
  // (in FormItem). The validation messages use the same renamed text.
  const vocab = useVocab();

  // -----------------------------------------------------------------------
  // Sync selectedAssociate → form
  // -----------------------------------------------------------------------
  useEffect(() => {
    form.setFieldsValue(
      selectedAssociate
        ? {
            lName:        selectedAssociate.lName,
            fName:        selectedAssociate.fName,
            mName:        selectedAssociate.mName,
            suffix:       selectedAssociate.suffix,
            daytimePhone: selectedAssociate.daytimePhone,
            phone:        selectedAssociate.phone,
          }
        : { lName: "", fName: "", mName: "", suffix: "", daytimePhone: "", phone: "" }
    );

    // These values came from the jacket the user just selected, not from
    // typing — pink them out until the user confirms each one (edit or Ctrl+Q).
    if (selectedAssociate && fromJacketSearch) {
      const copiedFields = JACKET_COPIED_FIELDS.filter((f) => selectedAssociate[f]);
      if (copiedFields.length) markUnverified(copiedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssociate, form, fromJacketSearch]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <StyledGridRow gutter={16}>

      {/* Last Name */}
      <StyledGridCol span={4}>
        <SecureFormItem
          fieldName="lName"
          formItemProps={{
            name: "lName", label: "Last Name",
            required: !fullSearch,
            rules: !fullSearch ? [{ required: true, message: "Last Name is required" }] : [],
          }}
        >
          <TextField />
        </SecureFormItem>
      </StyledGridCol>

      {/* First Name */}
      <StyledGridCol span={4}>
        <SecureFormItem
          fieldName="fName"
          formItemProps={{
            name: "fName", label: "First Name",
            required: !fullSearch,
            rules: !fullSearch ? [{ required: true, message: "First Name is required" }] : [],
          }}
        >
          <TextField />
        </SecureFormItem>
      </StyledGridCol>

      {/* Middle Name */}
      <StyledGridCol span={4}>
        <SecureFormItem
          fieldName="mName"
          formItemProps={{ name: "mName", label: "Middle Name" }}
        >
          <TextField />
        </SecureFormItem>
      </StyledGridCol>

      {/* Suffix */}
      <StyledGridCol span={3}>
        <SecureFormItem
          fieldName="suffix"
          formItemProps={{ name: "suffix", label: "Suffix" }}
        >
          <Select
            options={getOptionsFromRefList("Suffix")}
            placeholder="Suffix"
            showSearch={false}
            variant="outlined"
          />
        </SecureFormItem>
      </StyledGridCol>

      {/* Daytime Phone */}
      <StyledGridCol span={4}>
        <SecureFormItem
          fieldName="daytimePhone"
          formItemProps={{
            name: "daytimePhone", label: "Daytime Phone",
            rules: [
              { pattern: phonePattern, message: phoneMessage },
              ...generateRules("Bio:Daytime_Phone", vocab("Daytime Phone", "Daytime_Phone"), false),
            ],
          }}
        >
          <TextField />
        </SecureFormItem>
      </StyledGridCol>

      {/* Evening Phone */}
      <StyledGridCol span={4}>
        <SecureFormItem
          fieldName="phone"
          formItemProps={{
            name: "phone", label: "Evening Phone",
            rules: [
              { pattern: phonePattern, message: phoneMessage },
              ...generateRules("Bio:Phone", vocab("Evening Phone", "Phone"), false),
            ],
          }}
        >
          <TextField />
        </SecureFormItem>
      </StyledGridCol>

    </StyledGridRow>
  );
};

export default PersonalBasicInformation;